import React, { useState, useEffect } from "react";
import { AlertTriangle, Send, X } from "lucide-react";
import { createPortal } from "react-dom";

export default function EmergencyAlertModal({ isOpen, onClose, onSend, isSending }) {
  const [selectedReason, setSelectedReason] = useState("");
  const [description, setDescription] = useState("");

  // Lock body scroll and disable map interactions when modal is open
  useEffect(() => {
    if (isOpen) {
      // Lock body scroll
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.classList.add('emergency-modal-open');
      
      // Force disable map interactions
      const mapContainer = document.querySelector('.leaflet-container');
      if (mapContainer) {
        mapContainer.style.setProperty('pointer-events', 'none', 'important');
        mapContainer.style.setProperty('z-index', '0', 'important');
      }
      
      // Disable all Leaflet controls
      const leafletControls = document.querySelectorAll('.leaflet-control-zoom, .leaflet-control-attribution, .leaflet-control-container');
      leafletControls.forEach(control => {
        control.style.setProperty('pointer-events', 'none', 'important');
        control.style.setProperty('opacity', '0.3', 'important');
      });
      
    } else {
      // Restore body scroll
      document.body.style.overflow = 'unset';
      document.body.style.position = 'unset';
      document.body.style.width = 'unset';
      document.body.classList.remove('emergency-modal-open');
      
      // Restore map interactions
      const mapContainer = document.querySelector('.leaflet-container');
      if (mapContainer) {
        mapContainer.style.removeProperty('pointer-events');
        mapContainer.style.removeProperty('z-index');
      }
      
      // Restore Leaflet controls
      const leafletControls = document.querySelectorAll('.leaflet-control-zoom, .leaflet-control-attribution, .leaflet-control-container');
      leafletControls.forEach(control => {
        control.style.removeProperty('pointer-events');
        control.style.removeProperty('opacity');
      });
    }
    
    return () => {
      document.body.style.overflow = 'unset';
      document.body.style.position = 'unset';
      document.body.style.width = 'unset';
      document.body.classList.remove('emergency-modal-open');
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const reasons = [
    { code: "E1", label: "Accident" },
    { code: "E2", label: "Vehicle Breakdown" },
    { code: "E3", label: "Medical Emergency" },
    { code: "E4", label: "Security Threat" },
    { code: "E5", label: "Other" },
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    // Reason is mandatory
    if (!selectedReason) {
      alert("PLEASE SELECT A REASON (E1-E5). You cannot send an empty alert.");
      return;
    }
    
    onSend({ 
      ecode: selectedReason, 
      ediscription: description || "" 
    });
  };

  // Use portal to render modal at the root level
  const modalContent = (
    <div 
      className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-70 p-4"
      style={{ 
        zIndex: 10000,
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.7)'
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget && !isSending) {
          onClose();
        }
      }}
    >
      <div 
        className="relative w-full max-w-lg rounded-xl bg-white shadow-2xl overflow-hidden ring-4 ring-red-500 animate-in fade-in zoom-in duration-300"
        style={{ zIndex: 10001 }}
      >
        {/* Header */}
        <div className="flex items-center justify-between bg-red-600 px-6 py-4 text-white">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-6 w-6 animate-pulse" />
            <span className="text-xl font-bold uppercase tracking-wider">Emergency Alert</span>
          </div>
          <button 
            onClick={onClose} 
            disabled={isSending} 
            className="rounded-full p-1 hover:bg-red-700 transition disabled:opacity-50"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <p className="mb-4 text-sm text-gray-500 font-medium">
            <span className="text-red-600 font-bold">* Required: </span> 
            Select an emergency reason below to activate the send button.
          </p>

          {/* Reason Buttons */}
          <div className="mb-6 grid grid-cols-2 gap-3">
            {reasons.map((r) => (
              <button
                key={r.code}
                type="button"
                onClick={() => setSelectedReason(r.code)}
                disabled={isSending}
                className={`flex flex-col items-center justify-center rounded-lg border-2 p-3 transition-all ${
                  selectedReason === r.code
                    ? "border-red-600 bg-red-50 text-red-700 shadow-sm"
                    : "border-gray-200 bg-gray-50 text-gray-600 hover:border-gray-300 hover:bg-gray-100"
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                <span className="text-sm font-bold">{r.code}</span>
                <span className="text-xs font-semibold">{r.label}</span>
              </button>
            ))}
          </div>

          {/* Description */}
          <div className="mb-6">
            <label className="mb-2 block text-sm font-medium text-gray-700">Additional Details / Message</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="e.g. Location context, severity, etc."
              className="w-full rounded-md border border-gray-300 p-3 text-sm focus:border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500"
              rows={3}
              disabled={isSending}
            />
          </div>

          {/* Actions */}
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              disabled={isSending}
              className="flex-1 rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={isSending || !selectedReason}
              className={`flex flex-[2] items-center justify-center gap-2 rounded-lg px-4 py-3 text-sm font-bold text-white shadow-md transition-all ${
                isSending || !selectedReason
                  ? "cursor-not-allowed bg-gray-400 opacity-70"
                  : "bg-red-600 hover:bg-red-700 active:scale-95"
              }`}
            >
              <Send className="h-4 w-4" />
              {isSending ? "Sending Alert..." : "SEND EMERGENCY ALERT"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // Use portal to render at document body level
  return createPortal(modalContent, document.body);
}